import { create } from 'zustand';

import { api, bootstrapSession, clearTokens, configureApi } from '@/lib/api';
import { ApiError } from '@/lib/api-errors';
import { clearSupportAccessToken, getSupportAccessToken } from '@/lib/support-access';
import { useChurchStore } from '@/stores/church-store';

/**
 * Who is signed in.
 *
 * The session itself lives in httpOnly cookies the browser holds and this store
 * cannot read, which is the point: nothing here is a credential, so nothing here can
 * be stolen from local storage. On every load we probe the API (after a proactive
 * refresh) so cookies alone are enough.
 */

export interface AuthenticatedUser {
  id: string;
  email: string;
  phone: string | null;
  firstName: string;
  lastName: string;
  avatarKey: string | null;
  avatarUrl: string | null;
  locale: string;
  timezone: string;
  platformRole: 'none' | 'platform_manager' | 'platform_admin';
  status: 'active' | 'suspended' | 'banned' | 'deactivated';
  isEmailVerified: boolean;
  isPhoneVerified: boolean;
  isTotpEnabled: boolean;
  hasPassword: boolean;
  lastSignInAt: string | null;
  createdAt: string;
  /** Whether this console session skips the idle timeout until refresh expiry. */
  sessionRememberMe?: boolean;
  rememberMeEnabled?: boolean;
  idleTimeoutMinutes?: number;
  supportAccess?: {
    id: string;
    churchId: string;
    expiresAt: string;
  };
}

/**
 * `unknown` is the state before the first `/auth/me` answers, and it is deliberately
 * distinct from `signedOut`. Treating them the same is what makes a console redirect
 * a signed-in user to the sign-in page for a moment on every hard refresh.
 */
type Status = 'unknown' | 'authenticated' | 'signedOut';

interface AuthState {
  status: Status;
  user: AuthenticatedUser | null;
  setUser: (user: AuthenticatedUser) => void;
  /** Reads the current session from the API. Called once when the app mounts. */
  restore: () => Promise<void>;
  signOut: () => Promise<void>;
  /** Session ended server-side: clear local state without another network call. */
  clear: () => void;
}

export const useAuthStore = create<AuthState>((set, get) => ({
  status: 'unknown',
  user: null,

  setUser: (user) => {
    set({ user, status: 'authenticated' });
  },

  restore: async () => {
    try {
      if (!getSupportAccessToken()) await bootstrapSession();
      const user = await api.get<AuthenticatedUser>('/auth/me', { withoutChurch: true });
      set({ user, status: 'authenticated' });
    } catch (error) {
      // Only a real unauthenticated response means the cookies are gone. A cache miss,
      // 304, or network blip must not wipe a session the browser still holds.
      if (error instanceof ApiError && error.status === 401) {
        useChurchStore.getState().reset();
        set({ user: null, status: 'signedOut' });
        return;
      }
      if (get().status === 'authenticated') {
        return;
      }
      // Stay on the loading gate — RequireAuth keeps waiting rather than bouncing
      // to sign-in when /auth/me failed for a transient reason.
    }
  },

  signOut: async () => {
    try {
      if (getSupportAccessToken()) {
        await api.post('/support-access/end', undefined, { withoutChurch: true });
      } else {
        await api.post('/auth/logout', undefined, { withoutChurch: true });
      }
    } finally {
      // Local state clears even if the call failed. A person who clicked Sign out and
      // stayed signed in has been ignored, and on a shared church computer that
      // matters more than a tidy error.
      clearSupportAccessToken();
      clearTokens();
      useChurchStore.getState().reset();
      set({ user: null, status: 'signedOut' });
    }
  },

  clear: () => {
    clearSupportAccessToken();
    clearTokens();
    useChurchStore.getState().reset();
    set({ user: null, status: 'signedOut' });
  },
}));

/**
 * Lets the api client end the session when a silent refresh fails, without importing
 * the store into it and creating a cycle.
 */
configureApi({
  onSessionLost: () => {
    useAuthStore.getState().clear();
  },
});
